import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Button, Container } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import Body from '../components/Body';
import Image from '../components/Image';
import LoginDialog from '../forms/LoginDialog';
import { useAuth } from '../data/auth/token';
import HideUserByTokenMutation from '../data/mutations/HideUserByTokenMutation';
import UnhideUserByTokenMutation from '../data/mutations/UnhideUserByTokenMutation';

type ViewState = 'initial' | 'hidden';

export default function Hide() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  // The sunset token is a userId or base64-encoded email — a logged-in
  // user's own id works as a token, so fall back to it when the URL has none.
  const token = searchParams.get('token') || auth?.user?._id || '';
  const [view, setView] = useState<ViewState>('initial');
  const [submitting, setSubmitting] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const [hideUser] = useMutation(HideUserByTokenMutation);
  const [unhideUser] = useMutation(UnhideUserByTokenMutation);

  const performHide = async (actionToken: string) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await hideUser({ variables: { token: actionToken, language: i18n.language } });
      setView('hidden');
    } finally {
      setSubmitting(false);
    }
  };

  const performUnhide = async (actionToken: string) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await unhideUser({ variables: { token: actionToken } });
      setView('initial');
    } finally {
      setSubmitting(false);
    }
  };

  const isHidden = view === 'hidden';

  const handleClick = () => {
    if (!token) {
      // No token in the URL and not logged in — log in to act on own profile.
      setShowLogin(true);
      return;
    }
    if (isHidden) {
      performUnhide(token);
    } else {
      performHide(token);
    }
  };

  const titleKey = isHidden ? 'sunset.hide.confirmed_title' : 'sunset.hide.initial_title';
  const bodyKey = isHidden ? 'sunset.hide.confirmed_body' : 'sunset.hide.initial_body';
  const ctaKey = isHidden ? 'sunset.hide.confirmed_cta' : 'sunset.hide.initial_cta';

  return (
    <>
      <Helmet>
        <title>{t(titleKey)} - Relate</title>
      </Helmet>
      <Body>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Image src="/logo.png" sx={{ width: 65 }} />
        </Box>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Box sx={{ color: 'white', textAlign: 'center' }}>
            <Typography variant="h2" gutterBottom>
              {t(titleKey)}
            </Typography>
            <Typography variant="body1" sx={{ mb: 5, opacity: 0.9, whiteSpace: 'pre-line' }}>
              {t(bodyKey)}
            </Typography>
            <Button
              variant="contained"
              onClick={handleClick}
              disabled={submitting}
              size="large"
              sx={{ minWidth: 240, minHeight: 48 }}
            >
              {t(ctaKey)}
            </Button>
          </Box>
        </Container>
        {showLogin && (
          <LoginDialog
            open
            handleClose={() => setShowLogin(false)}
            onLoggedIn={(user) => {
              setShowLogin(false);
              if (!user?._id) return;
              if (isHidden) {
                performUnhide(user._id);
              } else {
                performHide(user._id);
              }
            }}
          />
        )}
      </Body>
    </>
  );
}
